import AppContext from 'App/AppContext';
import { IPDefaultButton, IPLabel, IPText } from 'baseComponents';
import { IPStack } from 'baseComponents/Stack/IPStack';
import { IPToggle } from 'baseComponents/Toggle/IPToggle';
import HrefLink from 'components/Links/HrefLink';
import AddNewListPanel from 'components/lists/AddNewListPanel';
import TextInfo from 'components/Notification/Info';
import { CategoryMenuButton, isEligibleCategory } from 'components/OutputLocations/CategoryMenu';
import OutputFolderPicker, { canPickOutputFolder } from 'components/OutputLocations/OutputFolderPicker';
import { createCategory } from 'components/OutputLocations/outputLocationActions';
import { globalStackTokensGapMedium, globalStackTokensGapSmall } from 'globalStyles';
import { DocumentProviderType, getProviderName } from 'models/documentProvider';
import DocumentOutputLocation, { DocumentOutputPurpose, kpiAttachmentProviders } from 'models/documentOutputLocation';
import ResourceList, { ResourceListType } from 'models/resourceList';
import { Fragment, useContext, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';

interface ISelectListAndFolderProps {
  listId: number | undefined;
  folderId: string | undefined;
  allowNavigation?: boolean;
  onUpdate: (listId: number | undefined, folderId: string | undefined) => void;
}

/**
 * Where the files attached to a KPI or a task-local question go. By default the tenant's KPI attachments location
 * (Admin, Settings, Reports) with its folder rule; turned off, a category of its own and optionally a folder in it,
 * which is part of the form definition. The folder is picked with the picker of the category's provider.
 */
const SelectListAndFolder = (props: ISelectListAndFolderProps) => {
  const appContext = useContext(AppContext);
  const { t } = useTranslation(['translation', 'kpis', 'forms', 'exceptions']);
  //off without a category yet: the user is choosing one, nothing is saved before the pick
  const [chooseOwn, setChooseOwn] = useState<boolean>(false);
  const [showFolderPicker, setShowFolderPicker] = useState<boolean>(false);
  const [folderName, setFolderName] = useState<string | undefined>(undefined);
  const [addCategory, setAddCategory] = useState<boolean>(false);

  const lists = appContext.globalDataCache.lists;
  const useOwn = chooseOwn || props.listId !== undefined;
  const selectedList = props.listId !== undefined ? lists.get(props.listId) : undefined;
  const tenantDefault = appContext.globalDataCache.outputLocations.getList(DocumentOutputPurpose.KpiAttachments);

  // the purpose's location, for the categories the menu offers; the editors do not read the catalogue
  const location = useMemo((): DocumentOutputLocation => {
    const output = new DocumentOutputLocation();
    output.purpose = DocumentOutputPurpose.KpiAttachments;
    output.allowedProviders = kpiAttachmentProviders;

    return output;
  }, []);

  const getDefaultText = (): string => {
    if (!tenantDefault) return t('forms:Fields.DefaultLocationNotSet');
    const provider = getProviderName(tenantDefault.providerType ?? DocumentProviderType.SharePoint);

    return t('forms:Fields.DefaultLocation', { location: `${tenantDefault.name} (${provider})` });
  };

  const setList = (list: ResourceList | undefined) => {
    setFolderName(undefined);
    setChooseOwn(false);
    props.onUpdate(list?.listId, undefined);
  };

  const onCategoryCreated = async (created: ResourceList) => {
    try {
      const saved = await createCategory(appContext, created);
      if (isEligibleCategory(location, saved)) {
        setList(saved);
      } else {
        appContext.showNotification(t('exceptions:OutputLocation.NotADocumentLibrary'), true, false, true);
      }
    } catch (err) {
      appContext.setError(err);
    }
  };

  //no folder: the attachments go into the category itself
  const setFolder = (folderId: string | undefined, name: string | undefined) => {
    setFolderName(name);
    props.onUpdate(props.listId, folderId);
  };

  //
  // Main render
  //
  return (
    <Fragment>
      <IPStack tokens={globalStackTokensGapMedium}>
        <IPStack.Item>
          <TextInfo message={t('forms:Fields.AttachmentLocationInfo')}>
            <IPLabel>{t('forms:Fields.AttachmentLocation')}</IPLabel>
          </TextInfo>
          <IPStack tokens={globalStackTokensGapSmall}>
            <IPToggle
              label={t('forms:Fields.UseDefaultLocation')}
              inlineLabel
              checked={!useOwn}
              onChange={(_ev, checked) => {
                if (checked) {
                  setList(undefined);
                } else {
                  setChooseOwn(true);
                }
              }}
            />
            <IPText>{getDefaultText()}</IPText>
            {useOwn && (
              <IPStack horizontal verticalAlign="center" tokens={globalStackTokensGapMedium}>
                <IPStack.Item>
                  <CategoryMenuButton
                    location={location}
                    selectedListId={props.listId}
                    onPick={(list: ResourceList) => setList(list)}
                    onCreateNew={() => setAddCategory(true)}
                  />
                </IPStack.Item>
                {props.allowNavigation && (
                  <IPStack.Item>
                    <HrefLink href="/library/lists">{t('forms:Fields.AttachmentListManageLink')}</HrefLink>
                  </IPStack.Item>
                )}
              </IPStack>
            )}
          </IPStack>
        </IPStack.Item>
        {useOwn && selectedList?.listType === ResourceListType.DocumentLibrary && (
          <IPStack.Item>
            <TextInfo message={t('forms:Fields.OverrideFolderInfo')}>
              <IPLabel>{t('forms:Fields.OverrideFolder')}</IPLabel>
            </TextInfo>
            <IPStack horizontal verticalAlign="center" tokens={globalStackTokensGapMedium}>
              <IPDefaultButton
                text={t('forms:Fields.OverrideFolderButton')}
                disabled={!canPickOutputFolder(selectedList)}
                onClick={() => setShowFolderPicker(true)}
              />
              <IPText block styles={{ root: { fontStyle: 'italic' } }}>
                {folderName}
              </IPText>
            </IPStack>
          </IPStack.Item>
        )}
      </IPStack>
      {showFolderPicker && selectedList && (
        <OutputFolderPicker
          list={selectedList}
          onSelect={(folderId, name) => setFolder(folderId, name)}
          onClose={() => setShowFolderPicker(false)}
        />
      )}
      {addCategory && (
        <AddNewListPanel
          isOpen={addCategory}
          renderAsPanel={false}
          onDismiss={() => setAddCategory(false)}
          onAddNew={(created: ResourceList) => onCategoryCreated(created)}
        />
      )}
    </Fragment>
  );
};

export default SelectListAndFolder;
